import { Theme } from "@mui/material";
import { darkPalette, defaultPalette, isColorValid } from "../../../utils/muiUtils";

/**
 * NG-8761: iframe documents are separate documents — they inherit neither the MUI theme nor the
 * `--oneh-*` custom properties defined on `:root` in `src/index.css`, and `<html data-color-mode>`
 * applies to the host page only. A template rendered through `srcDoc` therefore paints on the
 * browser's default white canvas in dark mode.
 *
 * The fix is to hand the iframe its own copy of the two colors that matter, resolved from the live
 * theme so the palette stays the single source of truth.
 */

export const COLOR_MODE_STYLE_MARKER = "data-oneh-color-mode";

/**
 * Second, in-CSS identity for the baseline. The marker ATTRIBUTE does not survive `sanitizer()`
 * (`htmlEditorUtils.ts`) — it allows only `style`/`colspan` on `*`, so `data-*` on a `<style>` is
 * stripped, while the style's text content passes through untouched. Nothing round-trips injected
 * markup back through the sanitizer today, but if anything ever does, an attribute-only guard would
 * stop matching and every re-injection would prepend ANOTHER baseline; the stale one, now later in
 * the document, would win on order and pin the frame to the previous mode.
 */
export const COLOR_MODE_CSS_SENTINEL = "/*oneh-color-mode*/";

const ALLOWED_COLOR_SCHEMES = ["light", "dark"] as const;

/**
 * `<style>` text content is serialized RAW — the HTML serializer does not escape it — and the result
 * of `withColorModeStyles` goes straight into `srcDoc` without passing through `sanitizer()`. A value
 * carrying `</style>` would therefore close the element and inject markup into the frame. The theme
 * values are palette literals today, but tenant `muiTheme` configs feed the same palette slots
 * (`generateTheme`), so validate rather than trust: anything CSS does not accept as a color is
 * replaced with the palette's own value for that mode.
 */
const safeColor = (value: string, fallback: string): string => (isColorValid(value) ? value : fallback);

/**
 * Baseline surface/ink for an iframe document, plus `color-scheme` so native controls (scrollbars,
 * form widgets, date pickers) inside the frame render their dark variants too.
 *
 * `background.paper`, not `background.default`: every one of these frames is embedded in a Dialog or
 * resizable Panel, i.e. an elevated surface. In dark mode the two differ (#1D2530 vs #141920) and the
 * canvas tone reads as a seam inside the panel; in light mode both are #fff, which is why the
 * mismatch is invisible until dark mode is switched on.
 */
export const buildIframeColorModeCss = (theme: Theme): string => {
    const isDark = theme.palette.mode === "dark";
    const fallbacks = isDark ? darkPalette : defaultPalette;
    const colorScheme = ALLOWED_COLOR_SCHEMES.includes(theme.palette.mode) ? theme.palette.mode : "light";
    const background = safeColor(theme.palette.background.paper, fallbacks.background.paper);
    const ink = safeColor(theme.palette.text.primary, fallbacks.text.primary);

    return `${COLOR_MODE_CSS_SENTINEL}:root{color-scheme:${colorScheme};}html,body{background-color:${background};color:${ink};}`;
};

/**
 * Finds the baseline by attribute first, then by the in-CSS sentinel, so a document whose attributes
 * were stripped is still recognised. Returns every match: duplicates are what pin a frame to a stale
 * mode, so callers collapse them.
 */
const findColorModeStyles = (root: Element): HTMLStyleElement[] => {
    const styles = Array.from(root.querySelectorAll("style"));
    return styles.filter((style) => style.hasAttribute(COLOR_MODE_STYLE_MARKER) || (style.textContent || "").includes(COLOR_MODE_CSS_SENTINEL));
};

// The subject of a selector — its last compound — is what the rule actually styles.
const ROOT_SUBJECT = /^(?:html|body|:root)(?![\w-])/i;
// Everything the `background` shorthand can carry that is NOT a color. What survives stripping these
// is treated as a color (a hex, a keyword like `white`, a gradient, a var()), which keeps the check
// biased the safe way: only a value that actually paints the canvas suppresses the baseline.
const NON_COLOR_SHORTHAND =
    /url\([^)]*\)|\b(?:no-repeat|repeat-x|repeat-y|repeat|space|round|fixed|local|scroll|border-box|padding-box|content-box|cover|contain|auto|center|top|bottom|left|right|none)\b|[\d.]+(?:px|em|rem|%|vh|vw|pt)?|[/,!]|\bimportant\b/gi; // NOSONAR(typescript:S5843): a flat keyword list: the score counts alternatives, nothing is nested

const declaresBackgroundColor = (declarations: string): boolean =>
    declarations.split(";").some((declaration) => {
        const separator = declaration.indexOf(":");
        if (separator < 0) {
            return false;
        }

        const property = declaration.slice(0, separator).trim().toLowerCase();
        const value = declaration.slice(separator + 1).trim();
        if (!value) {
            return false;
        }

        if (property === "background-color") {
            return true;
        }

        // `background: url(bg.png) no-repeat` paints an image, not the canvas colour — the baseline
        // surface still has to be injected underneath it.
        return property === "background" && value.replace(NON_COLOR_SHORTHAND, "").trim().length > 0;
    });

/**
 * True when a rule set assigns a background to the document root itself. Matched on the SUBJECT of the
 * selector (its last compound), so `body`, `body.light` and `html body` all count while `body > div`
 * does not — the descendant is the template styling its own content, not the canvas.
 *
 * Deliberately crude regex parsing: a false positive costs the frame its themed surface (readable,
 * just untinted), a false negative leaves our ink on the author's background, which is the unreadable
 * outcome.
 */
const declaresRootBackground = (css: string): boolean =>
    Array.from(css.matchAll(/([^{}]+)\{([^{}]*)\}/g)).some(
        ([, selectors, declarations]) =>
            declaresBackgroundColor(declarations) &&
            selectors.split(",").some((selector) => {
                const subject =
                    selector
                        .trim()
                        .split(/[\s>+~]+/)
                        .pop() || "";
                return ROOT_SUBJECT.test(subject);
            }),
    );

/**
 * The baseline is all-or-nothing. Shipping the surface and the ink as one rule means a template that
 * sets only `body{background:#fff}` overrides the background and keeps our dark ink — Cloud text on
 * white, worse than the bug being fixed. So when the author has already claimed the document
 * background, hand the whole document back to them: their background, the browser's default ink.
 */
const declaresOwnBackground = (doc: Document): boolean => {
    const roots = [doc.documentElement, doc.body].filter(Boolean) as Element[];
    // Inline styles go through the same value check, so `style="background:url(bg.png)"` does not
    // suppress the baseline either.
    if (roots.some((root) => declaresBackgroundColor(root.getAttribute("style") || ""))) {
        return true;
    }

    const ownStyles = new Set(findColorModeStyles(doc.documentElement));
    return Array.from(doc.querySelectorAll("style"))
        .filter((style) => !ownStyles.has(style))
        .some((style) => declaresRootBackground(style.textContent || ""));
};

/**
 * Inserts the baseline as the FIRST node in `<head>` so it acts as a default: an author-supplied
 * template that sets its own colors still wins on specificity/order. That is deliberate — those
 * templates are user content, and silently overriding their styling would be a worse bug than the
 * one being fixed.
 *
 * Idempotent by REPLACING an existing baseline rather than skipping it: a presence-only guard would
 * pin the frame to whichever mode injected first, so a document that already carries one (the
 * ResponsiveIframe load-handler running after the `srcDoc` pass, or a mode toggle re-injecting into
 * the live frame) would keep serving the stale mode's colors. Any extra copies are dropped and the
 * survivor is moved back to first position, since neither is guaranteed after a third party has
 * rewritten the document.
 */
export const prependColorModeStyle = (doc: Document, css: string): void => {
    const head = doc.querySelector("head");
    if (!head) {
        return;
    }

    const [existing, ...duplicates] = findColorModeStyles(head);
    duplicates.forEach((duplicate) => duplicate.remove());

    // Checked after the baseline is located so our own rule is never mistaken for the author's.
    if (declaresOwnBackground(doc)) {
        existing?.remove();
        return;
    }

    if (existing) {
        existing.setAttribute(COLOR_MODE_STYLE_MARKER, "true");
        existing.textContent = css;
        head.prepend(existing);
        return;
    }

    const style = doc.createElement("style");
    style.setAttribute(COLOR_MODE_STYLE_MARKER, "true");
    style.textContent = css;
    head.prepend(style);
};

/**
 * Standalone templates are always written as `<!DOCTYPE html>` (see `toStandaloneHtmlDocument` in
 * `htmlEditorUtils.ts`, which is what makes a doctype reach this function at all), so the doctype is
 * re-emitted as that one form rather than reconstructed from the parsed node: a legacy PUBLIC/SYSTEM
 * doctype cannot be produced by the save path, and re-quoting author-supplied ids would mean escaping
 * a quote that could otherwise break out of the doctype token.
 *
 * `documentElement.outerHTML` omits the doctype, and a template that arrives as a full
 * `<!DOCTYPE html>` document would be handed back without one — which drops the frame into quirks
 * mode and silently changes the box model, percentage heights and line-height. Re-emit it.
 */
export const serializeDocument = (doc: Document): string => (doc.doctype ? `<!DOCTYPE html>${doc.documentElement.outerHTML}` : doc.documentElement.outerHTML);

// A literal, not new RegExp(`…${COLOR_MODE_STYLE_MARKER}…`): the marker is inlined so the pattern
// cannot be broken by escaping. "replaces a stale baseline" in the tests pins it against the constant.
const BASELINE_STYLE = /<style[^>]*data-oneh-color-mode[^>]*>[\s\S]*?<\/style>/gi;
const AUTHOR_STYLE_BLOCK = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
const ROOT_INLINE_STYLE = /<(?:html|body)\b[^>]*\sstyle\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
const HEAD_OPEN = /<head\b[^>]*>/i;
const HTML_OPEN = /<html\b[^>]*>/i;

/**
 * Markup-level twin of `declaresOwnBackground`, for the string path below where there is no tree to
 * query. Our own baseline is excluded by marker and by sentinel, so it can never be read as the
 * author claiming the canvas.
 */
const declaresOwnBackgroundInMarkup = (html: string): boolean => {
    const inlineClaims = Array.from(html.matchAll(ROOT_INLINE_STYLE)).some((match) => declaresBackgroundColor(match[1] ?? match[2] ?? ""));
    if (inlineClaims) {
        return true;
    }

    return Array.from(html.matchAll(AUTHOR_STYLE_BLOCK)).some(
        ([block, contents]) => !block.includes(COLOR_MODE_STYLE_MARKER) && !contents.includes(COLOR_MODE_CSS_SENTINEL) && declaresRootBackground(contents),
    );
};

/**
 * `srcDoc` string variant, for the raw iframes that do not go through ResponsiveIframe.
 *
 * Inserts the baseline by STRING surgery rather than parsing. These are Handlebars templates: the HTML
 * tree builder discards `<tr>`/`<td>` outside a `<table>` and foster-parents a `{{#each}}` out of the
 * table it wraps, so a parse-and-re-serialize pass silently rewrote the very template it was previewing
 * (these two call sites passed `src` to `srcDoc` untouched before NG-8761). Keeping it textual also
 * makes the frame receive the stored file byte for byte, doctype included, plus one injected node.
 *
 * Returns the input unchanged when it is empty, so an unloaded preview stays empty rather than
 * becoming a bare `<html>` skeleton.
 */
export const withColorModeStyles = (htmlString: string, css: string): string => {
    if (!htmlString) {
        return htmlString;
    }

    // Any baseline from an earlier pass goes first, so a mode change cannot leave a stale copy behind.
    const template = htmlString.replace(BASELINE_STYLE, "");
    if (declaresOwnBackgroundInMarkup(template)) {
        return template;
    }

    const style = `<style ${COLOR_MODE_STYLE_MARKER}="true">${css}</style>`;

    if (HEAD_OPEN.test(template)) {
        return template.replace(HEAD_OPEN, (headTag) => `${headTag}${style}`);
    }
    if (HTML_OPEN.test(template)) {
        return template.replace(HTML_OPEN, (htmlTag) => `${htmlTag}<head>${style}</head>`);
    }

    // A bare fragment: the parser hoists a leading <style> into the head it synthesizes.
    return `${style}${template}`;
};
